/**
 * Format date to DD-MM-YYYY format
 * @param date Date string or Date object
 * @returns formatted date string in DD-MM-YYYY format
 */
export const formatDate = (date: string | Date): string => {
  const dateObj = date instanceof Date ? date : new Date(date);
  return dateObj.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).split('/').join('-');
};

/**
 * Format currency in Indian Rupee format
 * @param amount number to format as currency
 * @returns formatted currency string with ₹ symbol
 */
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

/**
 * Convert a number to words (Indian numbering system)
 * @param num - The number to convert
 * @returns The number in words
 */
export const numberToWords = (num: number): string => {
  const single = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
  const double = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  const formatTriplet = (num: number): string => {
    if (num === 0) return '';

    let result = '';

    // Handle hundreds
    if (Math.floor(num / 100) > 0) {
      result += single[Math.floor(num / 100)] + ' Hundred';
      if (num % 100 > 0) result += ' and ';
    }

    // Handle tens and ones
    const tensOnes = num % 100;
    if (tensOnes < 10) {
      result += single[tensOnes];
    } else if (tensOnes < 20) {
      result += double[tensOnes - 10];
    } else {
      result += tens[Math.floor(tensOnes / 10)];
      if (tensOnes % 10 > 0) result += ' ' + single[tensOnes % 10];
    }

    return result;
  };

  if (num === 0) return 'Zero';

  // Handle Indian numbering system (Lakhs and Crores)
  const crore = Math.floor(num / 10000000);
  const lakh = Math.floor((num % 10000000) / 100000);
  const thousand = Math.floor((num % 100000) / 1000);
  const remainder = Math.floor(num % 1000);

  let result = '';

  if (crore > 0) {
    result += formatTriplet(crore) + ' Crore';
    if (lakh > 0 || thousand > 0 || remainder > 0) result += ' ';
  }

  if (lakh > 0) {
    result += formatTriplet(lakh) + ' Lakh';
    if (thousand > 0 || remainder > 0) result += ' ';
  }

  if (thousand > 0) {
    result += formatTriplet(thousand) + ' Thousand';
    if (remainder > 0) result += ' ';
  }

  if (remainder > 0) {
    result += formatTriplet(remainder);
  }

  return result;
};

/**
 * Format a phone number with proper spacing
 * @param phone The phone number to format
 * @returns Formatted phone number
 */
export const formatPhone = (phone: string): string => {
  if (!phone) return '';

  // Remove all non-numeric characters
  const numericPhone = phone.replace(/\D/g, '');

  // Format as per Indian phone numbers (10 digits)
  if (numericPhone.length === 10) {
    return numericPhone.replace(/(\d{5})(\d{5})/, '$1 $2');
  }

  // For numbers with country code
  if (numericPhone.length > 10) {
    const countryCode = numericPhone.slice(0, numericPhone.length - 10);
    const number = numericPhone.slice(-10);
    return `+${countryCode} ${number.replace(/(\d{5})(\d{5})/, '$1 $2')}`;
  }

  return phone; // Return original if can't format
};

/**
 * Formats a number with thousands separators
 * @param num - The number to format
 * @returns Formatted number string
 */
export const formatNumber = (num: number): string => {
  return new Intl.NumberFormat('en-IN').format(num);
};

/**
 * Calculates the amount for an invoice item
 * @param quantity - The quantity
 * @param rate - The rate/price per unit
 * @returns Calculated amount
 */
export const calculateLineItemAmount = (quantity: number = 0, rate: number = 0): number => {
  return Number(((quantity || 0) * (rate || 0)).toFixed(2));
}; 